import React, { useState } from "react";
import type { TranscriptItem } from "../../translator/hooks/useTranslator";
import type { Meeting } from "../hooks/useMeetingHistory";
import { useUiLang } from "../../../context/UiLangContext";
import { Calendar } from "lucide-react";
import { useMeetingHistory } from "../hooks/useMeetingHistory";

interface TranslationHistoryProps {
  translatorServiceUrl: string;
  recording: boolean;
  onOpenSavedSummary: (meeting: Meeting, summaryText: string, htmlFilename?: string) => void;
}

export const TranslationHistory: React.FC<TranslationHistoryProps> = ({
  translatorServiceUrl,
  recording,
  onOpenSavedSummary,
}) => {
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";
  const { meetings: pastMeetings, loadingMeetingId, loadMeetingSummary, fetchMeetings } = useMeetingHistory(translatorServiceUrl);

  React.useEffect(() => {
    fetchMeetings("", isEn);
  }, [fetchMeetings, isEn]);

  const prevRecordingRef = React.useRef(recording);
  React.useEffect(() => {
    if (prevRecordingRef.current && !recording) {
      setTimeout(() => fetchMeetings("", isEn), 1000);
    }
    prevRecordingRef.current = recording;
  }, [recording, fetchMeetings, isEn]);

  const handleLoad = async (meeting: Meeting) => {
    await loadMeetingSummary(meeting, recording, isEn, onOpenSavedSummary);
  };

  const formatDate = (isoString: string) => {
    const d = new Date(isoString);
    return isNaN(d.getTime()) ? isoString : d.toLocaleDateString(isEn ? "en-US" : "es-ES", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <div className="w-full lg:w-[250px] flex-shrink-0 flex flex-col bg-[var(--sidebar-bg)] backdrop-blur-md border-b lg:border-b-0 lg:border-r border-[var(--border)] p-[16px] overflow-hidden max-h-[35vh] lg:max-h-none order-2 lg:order-1 transition-colors duration-300">
      <p className="text-[11px] font-semibold tracking-[0.05em] text-[var(--text-dim)] uppercase mb-[12px] pl-[4px]">
        {isEn ? "Meeting History" : "Historial de Reuniones"}
      </p>

      <div className="flex-1 overflow-y-auto space-y-[8px] pr-1 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
        {pastMeetings.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-4">
            <Calendar className="w-8 h-8 text-[var(--text-dim)] opacity-50 mb-3" />
            <p className="text-[12px] text-[var(--text-dim)] font-medium">
              {isEn ? "Record your first meeting to see it here." : "Grabá tu primera reunión para verla aquí."}
            </p>
          </div>
        ) : (
          pastMeetings.map((meeting) => (
              <div
                key={meeting._id}
                onClick={() => handleLoad(meeting)}
                className="py-[10px] px-[12px] bg-[var(--surface)] hover:bg-white/[0.06] border border-[var(--border)] rounded-[8px] cursor-pointer transition-all duration-200 hover:border-[var(--accent)] flex flex-col relative"
                role="button"
                tabIndex={0}
              >
                {loadingMeetingId === meeting._id && (
                  <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center rounded-[8px] z-10">
                    <div className="w-4 h-4 border-2 border-accent/20 border-t-accent rounded-full animate-spin" />
                  </div>
                )}
                <div className="flex justify-between items-start gap-2 mb-1">
                  <p className="text-[12px] font-semibold text-[var(--text)] line-clamp-2 leading-snug">
                    {meeting.title || (isEn ? "Untitled Meeting" : "Reunión sin título")}
                  </p>
                </div>
                <div className="flex justify-between items-end mt-1">
                  <span className="text-[10px] font-medium text-[var(--text-dim)]">{formatDate(meeting.created_at)}</span>
                  <div className="flex items-center gap-1 bg-white/5 rounded-full px-2 py-0.5 border border-white/5">
                    <span className="text-[9px] font-mono text-[var(--text-muted)] font-semibold uppercase">{meeting.source_lang === "auto" ? "MIX" : meeting.source_lang}</span>
                    <span className="text-[9px] text-[var(--text-dim)]">→</span>
                    <span className="text-[9px] font-mono text-[var(--text-muted)] font-semibold uppercase">{meeting.target_lang}</span>
                  </div>
                </div>
              </div>
          ))
        )}
      </div>
    </div>
  );
};
